

























import { Vue, Component, Prop } from "vue-property-decorator";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IQuickAction } from "@/models/actions";
import { IProvisionRequest } from "@/models/provisioning";
import { ProvisionRequestStatusCodes as PRSC } from "@/data/enums/contractProducts";

@Component
export default class RequestQuickActions extends Vue {
  // Props

  @Prop({ required: true })
  public request!: IProvisionRequest;

  @Prop({ default: true })
  public routeOpening!: boolean;

  // Data

  public isLoadingFields = false;
  public isLoadingConfigurationFields = false;
  public isResolving = false;

  // Computed

  public get requestId() {
    return this.request.id;
  }

  public get availableActions() {
    return this.$_.filter(this.actions, action => action.if);
  }

  public get actions(): IQuickAction[] {
    return [
      {
        label: this.$t("_action.mark_resolved"),
        method: this.resolveRequest,
        icon: "check",
        isProcessing: this.isResolving,
        if: this.canResolve()
      },
      {
        label: this.$t("_action.retry_request"),
        method: this.retryRequest,
        icon: "redo",
        isProcessing: this.isLoadingFields,
        if: this.canRetry()
      },
      {
        label: this.$t("_action.cancel_request"),
        method: this.openCancelRequestConfirm,
        icon: "times",
        if: this.canCancel()
      },
      {
        label: this.$t("_action.view_debug_data"),
        method: this.openViewLogModal,
        if: true
      },
      {
        label: this.$t("_action.view_fields"),
        method: this.openViewFieldsModal,
        isProcessing: this.isLoadingConfigurationFields,
        disabled:
          this.request.status?.code === PRSC.PROVISION_REQUEST_SCHEDULED,
        if: true
      },
      {
        label: this.$t("_action.view_payload"),
        method: this.openViewPayloadModal,
        if: this.$userCan("get_provision_request_payload"),
        disabled: this.request.status?.code === PRSC.PROVISION_REQUEST_SCHEDULED
      }
    ];
  }

  // Methods

  public async resolveRequest() {
    try {
      this.isResolving = true;

      await this.$store.dispatch(
        `data/${DataModules.PROVISIONING_REQUESTS}/resolveRequest`,
        { id: this.requestId }
      );

      this.$store.dispatch("toast/show", {
        message: this.$t("_.request_marked_resolved")
      });

      this.emitRefreshData();
      this.$emit("reloadModalData");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isResolving = false;
    }
  }

  public async retryRequest() {
    this.isLoadingFields = true;

    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_REQUESTS}/retryProvisionRequest`,
      {
        id: this.requestId,
        onSuccess: newRequest => {
          this.viewRequest(newRequest.id);

          this.emitRefreshData();
          this.$store.dispatch("toast/show", {
            message: this.$t("_.request_successfully_resent")
          });

          this.$emit("close");
        },
        onFinish: () => {
          this.isLoadingFields = false;
        }
      }
    );
  }

  public getRequestFields(requestId: IProvisionRequest["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_REQUESTS}/listRequestFields`,
      {
        id: requestId,
        vm: this,
        storeData: false,
        returnData: false,
        ignoreStored: false
      }
    );
  }

  public openCancelRequestConfirm() {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/openCancelRequestModal`,
      {
        requestId: this.requestId,
        onSuccess: () => {
          this.emitRefreshData();
          this.$emit("close");
        }
      }
    );
  }

  public async openViewFieldsModal() {
    try {
      this.isLoadingConfigurationFields = true;
      const { data } = await this.getRequestFields(this.requestId);
      if (data.length)
        this.$store.dispatch(
          `data/${DataModules.PROVISIONING_REQUESTS}/openViewFieldsValuesModal`,
          {
            props: { requestFields: data }
          }
        );
      else
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.provisioning.request_has_no_fields")
        });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoadingConfigurationFields = false;
    }
  }

  public openViewLogModal() {
    this.$store.dispatch(`data/${DataModules.LOGS}/openLogModal`, {
      props: {
        log: this.request,
        title: this.$t("_.provision_request_view_log", {
          name: this.request.function_name
        }),
        deep: 1
      }
    });
  }

  public openViewPayloadModal() {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_REQUESTS}/openPayloadModal`,
      {
        props: {
          requestId: this.requestId
        }
      }
    );
  }

  public emitRefreshData() {
    this.$emit("refreshData");
  }

  public canResolve() {
    return this.$store.getters[
      `data/${DataModules.PROVISIONING_REQUESTS}/canResolve`
    ](this.request);
  }

  public canRetry() {
    return this.$store.getters[
      `data/${DataModules.PROVISIONING_REQUESTS}/canRetry`
    ](this.request);
  }

  public canCancel() {
    return this.$store.getters[
      `data/${DataModules.PROVISIONING_REQUESTS}/canCancel`
    ](this.request);
  }

  public viewRequest(requestId) {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_REQUESTS}/openRequestModal`,
      {
        props: { requestId, routeOpening: this.routeOpening },
        events: {
          success: () => {
            this.emitRefreshData();
            this.$emit("close");
          }
        }
      }
    );
  }
}
